// ─── GeoAuditTool.jsx ────────────────────────────────────────────────────────
// GEO Page Audit Tool — AI Visibility Audit for any URL

import React, { useState } from "react";
import { apiFetch, friendlyError } from '../lib/apiFetch';
import { useLanguage } from "./LanguageContext";
import { downloadAuditPdf } from "./AuditPdfDocument";

// ─── Audit label translations (category labels + breakdown keys from AI data) ──
// Short labels for the small summary rings (≤52px)
const DE_RING_SHORT = {
  "Content Quality & Relevance":    "Inhalt",
  "Authority & Trustworthiness":    "Autorität",
  "Technical SEO & Page Structure": "Technik",
  "User Engagement":                "Nutzer",
};

const DE_AUDIT = {
  "Authority & Trustworthiness":    "Autorität & Vertrauenswürdigkeit",
  "User Engagement":                "Nutzerengagement",
  "Technical SEO & Page Structure": "Technisches SEO & Seitenstruktur",
  "Content Quality & Relevance":    "Inhaltsqualität & Relevanz",
  "Alt Text":                       "Alt-Texte",
  "Meta Tags":                      "Meta-Tags",
  "Accessibility":                  "Barrierefreiheit",
  "Page Structure":                 "Seitenstruktur",
  "Heading Structure":              "Überschriftenstruktur",
  "Natural Language":               "Natürliche Sprache",
  "Technical Details":              "Technische Details",
  "Accuracy And Currency":          "Aktualität & Genauigkeit",
  "Comprehensive Coverage":         "Vollständige Abdeckung",
  "External References":            "Externe Referenzen",
  "Testimonials":                   "Kundenbewertungen",
  "Legal Compliance":               "Rechtliche Konformität",
  "Social Presence":                "Social-Media-Präsenz",
  "Multimedia":                     "Multimedia",
  "Call To Action":                 "Call-to-Action",
  "Contact Options":                "Kontaktmöglichkeiten",
  "Structured Content":             "Strukturierter Inhalt",
  "Language Page":                  "Sprachseite",
  "Readability":                    "Lesbarkeit",
  "Interactivity":                  "Interaktivität",
  "Authority":                      "Autorität",
  "Trustworthiness":                "Vertrauenswürdigkeit",
};
const tde = (key) => DE_AUDIT[key] || key;

// ─── Score Ring ───────────────────────────────────────────────────────────────
function ScoreRing({ score, size = 72, strokeWidth = 7, label }) {
  const r = (size - strokeWidth) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - (score || 0) / 100);
  const color = score >= 75 ? "#16a34a" : score >= 50 ? "#f59e0b" : "#dc2626";
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke="#f1f5f9" strokeWidth={strokeWidth} />
        <circle cx={size/2} cy={size/2} r={r} fill="none" stroke={color} strokeWidth={strokeWidth}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.8s ease" }}
        />
        <text x={size/2} y={size/2} textAnchor="middle" dominantBaseline="central"
          style={{ transform: "rotate(90deg)", transformOrigin: `${size/2}px ${size/2}px` }}
          fontSize={size > 60 ? 18 : 13} fontWeight={700} fill={color}
        >{score ?? "—"}<tspan fontSize={size > 60 ? 10 : 7} fontWeight={400} opacity={0.6}>/100</tspan></text>
      </svg>
      {label && <div style={{ fontSize: 11, color: "#64748b", textAlign: "center", maxWidth: size + 20 }}>{label}</div>}
    </div>
  );
}

// ─── Score Bar ────────────────────────────────────────────────────────────────
function ScoreBar({ label, value }) {
  const color = value >= 75 ? "#16a34a" : value >= 50 ? "#f59e0b" : "#dc2626";
  return (
    <div style={{ marginBottom: 8 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3, fontSize: 12, color: "#475569" }}>
        <span>{label}</span>
        <span style={{ fontWeight: 700, color }}>{value}<span style={{ fontSize: 10, fontWeight: 400, opacity: 0.6 }}>/100</span></span>
      </div>
      <div style={{ height: 5, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
        <div style={{ height: "100%", width: `${value}%`, background: color, borderRadius: 3, transition: "width 0.6s ease" }} />
      </div>
    </div>
  );
}

// ─── Category Card ────────────────────────────────────────────────────────────
function CategoryCard({ data, icon, t }) {
  const [expanded, setExpanded] = useState(false);
  if (!data) return null;
  const color = data.score >= 75 ? "#16a34a" : data.score >= 50 ? "#f59e0b" : "#dc2626";
  const bgColor = data.score >= 75 ? "#f0fdf4" : data.score >= 50 ? "#fffbeb" : "#fef2f2";
  const borderColor = data.score >= 75 ? "#bbf7d0" : data.score >= 50 ? "#fde68a" : "#fecaca";

  return (
    <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, overflow: "hidden" }}>
      <div style={{ padding: "16px 20px", display: "flex", alignItems: "center", gap: 12 }}>
        <div style={{ width: 40, height: 40, borderRadius: 10, background: bgColor, border: `1px solid ${borderColor}`, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          {icon}
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{tde(data.label)}</div>
          <div style={{ marginTop: 4, height: 6, background: "#f1f5f9", borderRadius: 3, overflow: "hidden" }}>
            <div style={{ height: "100%", width: `${data.score}%`, background: color, borderRadius: 3, transition: "width 0.8s ease" }} />
          </div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 800, color, minWidth: 52, textAlign: "right" }}>{data.score}<span style={{ fontSize: 13, fontWeight: 400, opacity: 0.6 }}>/100</span></div>
      </div>

      {data.breakdown && (
        <div style={{ padding: "0 20px 14px", borderTop: "1px solid #f8fafc" }}>
          <div style={{ paddingTop: 12 }}>
            {Object.entries(data.breakdown).map(([key, val]) => (
              <ScoreBar key={key} label={tde(key.replace(/([A-Z])/g, " $1").replace(/^./, s => s.toUpperCase()))} value={val} />
            ))}
          </div>
        </div>
      )}

      <div style={{ borderTop: "1px solid #f1f5f9" }}>
        <button onClick={() => setExpanded(!expanded)} style={{
          width: "100%", padding: "10px 20px", background: "none", border: "none", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: 12, color: "#64748b",
        }}>
          <span style={{ fontWeight: 600 }}>{t("geo.findingsAndRecs")}</span>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
            style={{ transform: expanded ? "rotate(180deg)" : "none", transition: "transform 0.2s" }}>
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        {expanded && (
          <div style={{ padding: "0 20px 16px" }}>
            {data.findings?.length > 0 && (
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>{t("geo.findings")}</div>
                {data.findings.map((f, i) => (
                  <div key={i} style={{ display: "flex", gap: 8, marginBottom: 5, fontSize: 12, color: "#334155" }}>
                    <span style={{ color: "#94a3b8", flexShrink: 0 }}>◆</span><span>{f}</span>
                  </div>
                ))}
              </div>
            )}
            {data.recommendations?.length > 0 && (
              <div style={{ marginBottom: data.whyVisibility ? 12 : 0 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 6 }}>{t("geo.recommendations")}</div>
                {data.recommendations.map((r, i) => {
                  const rec = typeof r === "object" ? r : { text: r };
                  return (
                    <div key={i} style={{ marginBottom: 8 }}>
                      <div style={{ display: "flex", gap: 8, fontSize: 12, color: "#334155" }}>
                        <span style={{ color: "#16a34a", flexShrink: 0 }}>→</span>
                        <div>
                          {rec.title && <span style={{ fontWeight: 700 }}>{rec.title}: </span>}
                          <span>{rec.text || r}</span>
                          {rec.example && (
                            <div style={{ marginTop: 4, padding: "4px 8px", background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 4, fontSize: 11, color: "#166534" }}>
                              {rec.example}
                            </div>
                          )}
                        </div>
                      </div>
                      {(rec.impactScore != null || rec.effort) && (
                        <div style={{ display: "flex", gap: 6, marginTop: 3, marginLeft: 20 }}>
                          {rec.impactScore != null && <span style={{ fontSize: 10, padding: "1px 6px", background: "#eff6ff", color: "#2563eb", borderRadius: 3, fontWeight: 600 }}>+{rec.impactScore}</span>}
                          {rec.effort && <span style={{ fontSize: 10, padding: "1px 6px", background: "#f8fafc", color: "#64748b", borderRadius: 3 }}>{rec.effort}</span>}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
            {data.whyVisibility && (
              <div style={{ padding: "10px 12px", background: "#eff6ff", border: "1px solid #bfdbfe", borderRadius: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: "#2563eb", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 4 }}>{t("geo.whyVisibility")}</div>
                <div style={{ fontSize: 12, color: "#334155", lineHeight: 1.6 }}>{data.whyVisibility}</div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Main GeoAuditTool ────────────────────────────────────────────────────────
export default function GeoAuditTool({ session }) {
  const { t, lang } = useLanguage();
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [exporting, setExporting] = useState(false);

  const exportPdf = async () => {
    if (!result) return;
    setExporting(true);
    try { await downloadAuditPdf(result, lang); }
    catch (err) { console.error("PDF error:", err); alert("PDF Export fehlgeschlagen: " + err.message); }
    finally { setExporting(false); }
  };

  // Auto-normalize URL: add https:// if missing
  const normalizeUrl = (raw) => {
    const trimmed = raw.trim();
    if (!trimmed) return trimmed;
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return "https://" + trimmed;
  };

  const startAudit = async () => {
    if (!url.trim()) return;
    const normalizedUrl = normalizeUrl(url);
    if (normalizedUrl !== url) setUrl(normalizedUrl);
    setLoading(true);
    setError(null);
    setResult(null);

    const LOADING_STEPS = [
      t("geo.loadStep1"), t("geo.loadStep2"), t("geo.loadStep3"),
      t("geo.loadStep4"), t("geo.loadStep5"),
    ];

    let step = 0;
    setLoadingStep(LOADING_STEPS[0]);
    const stepInterval = setInterval(() => {
      step = Math.min(step + 1, LOADING_STEPS.length - 1);
      setLoadingStep(LOADING_STEPS[step]);
    }, 2500);

    try {
      const res = await apiFetch("/geo-audit", {
        method: "POST",
        body: JSON.stringify({ url: normalizedUrl, stealthMode: true, userId: session?.user?.id, lang }),
      });
      const data = await res.json();
      clearInterval(stepInterval);
      if (data.error) {
        setError(data.error);
      } else {
        setResult(data);
        setHistory(prev => [{ url: normalizedUrl, date: new Date().toISOString(), score: data.overallScore }, ...prev.slice(0, 9)]);
      }
    } catch (err) {
      clearInterval(stepInterval);
      setError(friendlyError(err, lang));
    } finally {
      setLoading(false);
    }
  };

  const CATEGORY_ICONS = {
    contentQuality: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
    authority:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#8b5cf6" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>,
    technicalSeo:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg>,
    engagement:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#10a37f" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>,
  };

  const checks = result?.structural ? [
    { label: t("geo.checkTitle"),    ok: result.structural.hasTitle },
    { label: t("geo.checkMetaDesc"), ok: result.structural.hasMetaDescription },
    { label: t("geo.checkH1"),       ok: result.structural.h1Count === 1 },
    { label: t("geo.checkAltText"),  ok: result.structural.imagesWithoutAlt === 0 },
    { label: t("geo.checkContact"),  ok: result.structural.hasContactForm },
    { label: t("geo.checkSocial"),   ok: result.structural.hasSocialLinks },
  ] : [];

  return (
    <div style={{ padding: "24px 28px", maxWidth: 1100, margin: "0 auto" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: "#0f172a", margin: 0 }}>{t("geo.pageTitle")}</h2>
          <p style={{ fontSize: 13, color: "#64748b", margin: "4px 0 0" }}>{t("geo.pageSubtitle")}</p>
        </div>
        {result && (
          <button onClick={exportPdf} disabled={exporting} className="no-print" style={{
            padding: "9px 18px", borderRadius: 8, border: "none", cursor: exporting ? "not-allowed" : "pointer",
            background: exporting ? "#e2e8f0" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
            color: exporting ? "#94a3b8" : "white", fontWeight: 700, fontSize: 13,
            display: "flex", alignItems: "center", gap: 8,
          }}>
            {exporting
              ? <><div style={{ width: 13, height: 13, border: "2px solid rgba(255,255,255,0.3)", borderTopColor: "white", borderRadius: "50%", animation: "spin 1s linear infinite" }} />Exportiere…</>
              : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>{t("geo.exportPdf")}</>
            }
          </button>
        )}
      </div>

      {/* Input Card — hidden after result is shown */}
      {!result && !loading && (
      <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px", marginBottom: 20 }}>
        <div style={{ display: "flex", gap: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 260 }}>
            <label style={{ fontSize: 12, fontWeight: 600, color: "#475569", display: "block", marginBottom: 6 }}>{t("geo.urlLabel")}</label>
            <input
              value={url} onChange={e => setUrl(e.target.value)}
              onKeyDown={e => e.key === "Enter" && !loading && startAudit()}
              placeholder={t("geo.urlPlaceholder")}
              disabled={loading}
              style={{ width: "100%", padding: "9px 12px", border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#0f172a", outline: "none", boxSizing: "border-box", background: loading ? "#f8fafc" : "white" }}
            />
          </div>
          <button onClick={startAudit} disabled={loading || !url.trim()} style={{
            padding: "9px 20px", borderRadius: 8, border: "none", cursor: loading || !url.trim() ? "not-allowed" : "pointer",
            background: loading || !url.trim() ? "#e2e8f0" : "linear-gradient(135deg, #2563eb, #1d4ed8)",
            color: loading || !url.trim() ? "#94a3b8" : "white", fontWeight: 700, fontSize: 13,
            display: "flex", alignItems: "center", gap: 8, transition: "all 0.2s",
          }}>
            <><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>{t("geo.startAudit")}</>
          </button>
        </div>
        <div style={{ marginTop: 10, fontSize: 11, color: "#94a3b8" }}>
          Credits: {t("geo.creditsInfo")}
        </div>
      </div>
      )}

      {/* "New Audit" link shown below result */}
      {result && !loading && (
        <div className="no-print" style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={() => { setResult(null); setError(null); setUrl(""); }} style={{ fontSize: 12, color: "#2563eb", background: "none", border: "none", cursor: "pointer", fontWeight: 600, display: "flex", alignItems: "center", gap: 4 }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
            {t("geo.newAudit")}
          </button>
          <span style={{ fontSize: 12, color: "#94a3b8" }}>{result.url}</span>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "40px 24px", textAlign: "center" }}>
          <div style={{ width: 48, height: 48, borderRadius: "50%", border: "3px solid #e2e8f0", borderTopColor: "#2563eb", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
          <div style={{ fontSize: 14, fontWeight: 600, color: "#0f172a", marginBottom: 6 }}>{loadingStep}</div>
          <div style={{ fontSize: 12, color: "#94a3b8" }}>{url}</div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div style={{ background: "#fef2f2", border: "1px solid #fecaca", borderRadius: 10, padding: "14px 18px", color: "#dc2626", fontSize: 13 }}>
          <strong>{t("geo.errorPrefix")}</strong> {error}
        </div>
      )}

      {/* Results */}
      {result && !loading && (
        <div>
          {/* Result Header */}
          <div style={{ background: "white", border: "1px solid #e2e8f0", borderRadius: 12, padding: "20px 24px", marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 20, flexWrap: "wrap" }}>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
                <ScoreRing score={result.overallScore} size={80} />
                <div style={{ fontSize: 11, color: "#64748b", fontWeight: 600, textAlign: "center" }}>{t("geo.scoreLabel")}</div>
              </div>
              <div style={{ flex: 1, minWidth: 220 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: "50%", background: result.overallScore >= 75 ? "#16a34a" : result.overallScore >= 50 ? "#f59e0b" : "#dc2626" }} />
                  <a href={result.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13, fontWeight: 600, color: "#2563eb", textDecoration: "none" }}>{result.url}</a>
                </div>
                <div style={{ fontSize: 12, color: "#475569", lineHeight: 1.6, marginBottom: 10 }}>{result.summary}</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {checks.map((item, i) => (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 11 }}>
                      <span style={{ color: item.ok ? "#16a34a" : "#dc2626", fontWeight: 700 }}>{item.ok ? "✓" : "✗"}</span>
                      <span style={{ color: "#64748b" }}>{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap", justifyContent: "flex-end" }}>
                {result.categories && Object.entries(result.categories).map(([key, cat]) => (
                  <ScoreRing key={key} score={cat.score} size={52} label={DE_RING_SHORT[cat.label] || cat.label.split(" ")[0]} />
                ))}
              </div>
            </div>
            {result.contentChangeSummary && (
              <div style={{ marginTop: 14, padding: "10px 14px", background: "#f8fafc", borderRadius: 8, border: "1px solid #e2e8f0", fontSize: 12, color: "#475569" }}>
                <span style={{ fontWeight: 700, color: "#0f172a" }}>{t("geo.contentChangeSummaryLabel")}</span>{result.contentChangeSummary}
              </div>
            )}
          </div>

          {/* Category grid */}
          {result.categories && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
              {Object.entries(result.categories).map(([key, cat]) => (
                <CategoryCard key={key} data={cat} icon={CATEGORY_ICONS[key]} t={t} />
              ))}
            </div>
          )}

        </div>
      )}
    </div>
  );
}
